import { useRef, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

import { OtpCodeInput } from './OtpCodeInput';

interface MfaChallengeStepProps {
  /** Called with the 6-digit code from the user's authenticator. */
  readonly onSubmit: (code: string) => Promise<void>;
  /** Inline error message from the parent page (code mismatch, expired, etc.). */
  readonly topError?: string | null;
}

const CODE_LENGTH = 6;

/**
 * Six-digit code entry for the SOFTWARE_TOKEN_MFA sign-in challenge.
 *
 * No "resend code" affordance — TOTP codes regenerate every 30 seconds on
 * the user's device, so there's nothing for the server to resend. If their
 * code keeps failing, the source-of-truth fix is either to wait 30 seconds
 * for a new code or to check their device's clock.
 */
export function MfaChallengeStep({ onSubmit, topError }: MfaChallengeStepProps) {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Same auto-submit-once guard as ConfirmCodeStep — clears on backspace
  // so the user can correct a single digit and re-fire.
  const autoSubmittedCodeRef = useRef<string | null>(null);

  const submit = async (codeToSubmit: string) => {
    if (codeToSubmit.length !== CODE_LENGTH || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(codeToSubmit);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (next: string) => {
    setCode(next);
    if (next.length === CODE_LENGTH && autoSubmittedCodeRef.current !== next) {
      autoSubmittedCodeRef.current = next;
      void submit(next);
    } else if (next.length < CODE_LENGTH) {
      autoSubmittedCodeRef.current = null;
    }
  };

  const canSubmit = code.length === CODE_LENGTH && !isSubmitting;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-foreground text-base font-semibold">Open your authenticator app</h3>
        <p className="text-muted-foreground text-sm">
          Enter the 6-digit code shown for Mailchute.ai. Codes refresh every 30 seconds.
        </p>
      </div>

      {topError ? (
        <Alert variant="destructive">
          <AlertDescription>{topError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="mfa-challenge-code-0">Verification code</Label>
        <OtpCodeInput
          id="mfa-challenge-code-0"
          value={code}
          onChange={handleChange}
          describedBy="mfa-challenge-hint"
        />
        <p id="mfa-challenge-hint" className="text-muted-foreground text-xs">
          Lost your authenticator? Contact support to reset MFA.
        </p>
      </div>

      <Button
        type="button"
        className="w-full"
        disabled={!canSubmit}
        onClick={() => void submit(code)}
      >
        {isSubmitting ? 'Verifying…' : 'Verify and sign in'}
      </Button>
    </div>
  );
}
